import { useState } from 'react';
import { Modal, Select, Textarea, Button, Stack, Group } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconCheck, IconFlag } from '@tabler/icons-react';
import { useApiClient } from '../../api';

// Report reason enums (contract §4.14) with human labels.
const REPORT_REASONS = [
  { value: 'spam', label: 'Spam' },
  { value: 'harassment', label: 'Harassment' },
  { value: 'scam', label: 'Scam' },
  { value: 'inappropriate_content', label: 'Inappropriate content' },
  { value: 'other', label: 'Other' },
];

// Modal to report a user (POST /api/v1/reports, contract §4.14).
// Reason is required; details are optional (≤ 2000 chars, empty → omitted).
const ReportUserModal = ({ opened, onClose, userId, displayName }) => {
  const api = useApiClient();
  const [reason, setReason] = useState(null);
  const [details, setDetails] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleClose = () => {
    setReason(null);
    setDetails('');
    onClose();
  };

  const handleSubmit = async () => {
    if (!reason || submitting) return;
    setSubmitting(true);
    try {
      const body = { reported_user_id: userId, reason };
      const trimmed = details.trim();
      if (trimmed) body.details = trimmed;
      await api.post('/api/v1/reports', body);
      notifications.show({
        message: 'Report submitted — our team will review it',
        color: 'green',
        icon: <IconCheck size="1rem" />,
      });
      handleClose();
    } catch (err) {
      notifications.show({
        title: 'Error',
        message: err?.message || 'Could not submit report',
        color: 'red',
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={`Report ${displayName || 'user'}`}
      centered
      transitionProps={{ duration: 0 }}
    >
      <Stack gap="md">
        <Select
          label="Reason"
          placeholder="Select a reason"
          data={REPORT_REASONS}
          value={reason}
          onChange={setReason}
          required
          comboboxProps={{ transitionProps: { duration: 0 } }}
        />
        <Textarea
          label="Details"
          placeholder="Anything that helps our team review this (optional)"
          value={details}
          onChange={(e) => setDetails(e.currentTarget.value)}
          maxLength={2000}
          autosize
          minRows={3}
        />
        <Group justify="flex-end" gap="sm">
          <Button variant="light" color="gray" onClick={handleClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            color="red"
            leftSection={<IconFlag size="1rem" />}
            onClick={handleSubmit}
            disabled={!reason}
            loading={submitting}
          >
            Submit report
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};

export default ReportUserModal;
